/**
 * Upgraded Spending Distribution Component
 * High-end Fintech UI with Center Metric Donut, Top-Spender Highlight, and Category Progress Bars.
 * @module components/dashboard/spending-distribution
 */

import { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import {
  PieChart,
  Footprints,
  Trees,
  Coins,
  TrendingUp,
  Sparkles,
  Layers,
} from "lucide-react";
import { parseApiFloat, formatCurrency } from "@/lib/utils/format";
import { t } from "@/lib/i18n/messages";
import type { Locale } from "@/types/auth";
import type { SpendingDistribution as SpendingDistributionType } from "@/types/wallet";
import { EmptyState } from "@/components/ui/empty-state";
import { cn } from "@/lib/utils";

// Register Chart.js elements
ChartJS.register(ArcElement, Tooltip, Legend);

interface SpendingDistributionProps {
  distribution: SpendingDistributionType[];
  locale: Locale;
  isDark: boolean;
  className?: string;
}

// Fallback palette for categories outside the Walk/Juthoor brand colors
const CATEGORY_COLORS = [
  "#F59E0B", // Amber 500
  "#8B5CF6", // Violet 500
  "#EC4899", // Pink 500
  "#06B6D4", // Cyan 500
];

function getCategoryColor(type: string, fallbackIdx: number): string {
  const lower = type.toLowerCase();
  if (lower.includes("walk") || lower.includes("خطى") || lower.includes("مشي")) {
    return "#0467C4"; // brand blue
  }
  if (
    lower.includes("juthoor") ||
    lower.includes("جذور") ||
    lower.includes("tree") ||
    lower.includes("شجر") ||
    lower.includes("green")
  ) {
    return "#ACE405"; // brand green
  }
  return CATEGORY_COLORS[fallbackIdx % CATEGORY_COLORS.length] ?? "#94A3B8";
}

function getCategoryIcon(type: string) {
  const lower = type.toLowerCase();
  if (lower.includes("walk") || lower.includes("خطى") || lower.includes("مشي")) {
    return Footprints;
  }
  if (
    lower.includes("juthoor") ||
    lower.includes("جذور") ||
    lower.includes("tree") ||
    lower.includes("شجر") ||
    lower.includes("green")
  ) {
    return Trees;
  }
  if (lower.includes("coin") || lower.includes("transfer") || lower.includes("fee")) {
    return Coins;
  }
  return Layers;
}

function getCategoryLabel(type: string, isRTL: boolean): string {
  const lower = type.toLowerCase();
  if (lower.includes("walk")) {
    return isRTL ? "مبادرة خُطى (المشي الرياضي)" : "Walk Initiative";
  }
  if (lower.includes("juthoor")) {
    return isRTL ? "مبادرة جذور (التشجير البيئي)" : "Juthoor Initiative";
  }
  return type;
}

export function SpendingDistribution({
  distribution,
  locale,
  isDark,
  className,
}: SpendingDistributionProps) {
  const isRTL = locale === "ar";

  // ── Compute totals and percentages ─────────────────────────────────────────
  const { totalAmount, totalCount, items, topItem } = useMemo(() => {
    if (!distribution || distribution.length === 0) {
      return { totalAmount: 0, totalCount: 0, items: [], topItem: null };
    }

    const parsedItems = distribution.map((item, idx) => {
      const amount = parseApiFloat(item.amount);
      return {
        ...item,
        numericAmount: amount,
        color: getCategoryColor(item.type ?? "", idx),
      };
    });

    const sumAmount = parsedItems.reduce((acc, it) => acc + it.numericAmount, 0);
    const sumCount = parsedItems.reduce((acc, it) => acc + (it.count || 0), 0);

    const itemsWithPercent = parsedItems.map((item) => ({
      ...item,
      percentage: sumAmount > 0 ? (item.numericAmount / sumAmount) * 100 : 0,
    }));

    // Find top-spending initiative
    const sorted = [...itemsWithPercent].sort(
      (a, b) => b.numericAmount - a.numericAmount
    );
    const topCandidate = sorted[0];
    const top = topCandidate && topCandidate.numericAmount > 0 ? topCandidate : null;

    return {
      totalAmount: sumAmount,
      totalCount: sumCount,
      items: itemsWithPercent,
      topItem: top,
    };
  }, [distribution]);

  // ── Chart.js Data & Options ────────────────────────────────────────────────
  const chartData = useMemo(() => {
    if (items.length === 0) return null;

    return {
      labels: items.map((it) => getCategoryLabel(it.type, isRTL)),
      datasets: [
        {
          data: items.map((it) => it.numericAmount),
          backgroundColor: items.map((it) => it.color),
          borderColor: isDark ? "#0F172A" : "#FFFFFF",
          borderWidth: 3,
          hoverOffset: 6,
        },
      ],
    };
  }, [items, isRTL, isDark]);

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "74%",
      plugins: {
        legend: {
          display: false, // We render a richer custom breakdown list
        },
        tooltip: {
          rtl: isRTL,
          backgroundColor: isDark
            ? "rgba(15, 23, 42, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          titleColor: isDark ? "#F8FAFC" : "#0F172A",
          bodyColor: isDark ? "#CBD5E1" : "#334155",
          borderColor: isDark ? "#334155" : "#E2E8F0",
          borderWidth: 1,
          padding: 12,
          cornerRadius: 10,
          callbacks: {
            label: (context: any) => {
              const label = context.label || "";
              const val = context.parsed ?? 0;
              const pct = totalAmount > 0 ? ((val / totalAmount) * 100).toFixed(1) : "0";
              return ` ${label}: ${formatCurrency(val, locale)} (${pct}%)`;
            },
          },
        },
      },
    };
  }, [isRTL, isDark, locale, totalAmount]);

  if (!chartData || items.length === 0) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-xs">
        <h3 className="text-lg font-bold text-foreground mb-4">
          {t(locale, "spendingDistribution")}
        </h3>
        <EmptyState
          icon={PieChart}
          title={t(locale, "noData")}
          description={t(locale, "noDataAvailable")}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-card p-5 md:p-7 shadow-xs space-y-6 h-full flex flex-col justify-between",
        className
      )}
    >
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-primary/10 text-primary">
              <PieChart className="w-4 h-4" />
            </span>
            <h2 className="text-base md:text-lg font-bold text-foreground">
              {isRTL ? "توزيع المصروفات بحسب المبادرات" : "Spending by Initiative"}
            </h2>
          </div>
          <p className="text-xs text-foreground-secondary mt-1">
            {isRTL
              ? "تحليل حصة كل مبادرة من إجمالي الميزانيات المنفقة."
              : "Allocation breakdown across all funded initiative programs."}
          </p>
        </div>

        {/* Total Spend Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/60 border border-border self-start sm:self-auto text-xs font-semibold text-foreground">
          <span>{isRTL ? "إجمالي المنصرف:" : "Total Spend:"}</span>
          <span className="text-primary font-bold">{formatCurrency(totalAmount, locale)}</span>
        </div>
      </div>

      {/* ─── 2-Column Responsive Layout ──────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center flex-1">
        {/* Left: Donut Chart with Center Metric Hole */}
        <div className="md:col-span-5 flex flex-col items-center justify-center relative py-2">
          <div className="w-56 h-56 sm:w-64 sm:h-64 relative flex items-center justify-center">
            <Doughnut data={chartData} options={chartOptions} />

            {/* Center Hole Metric Overlay */}
            <div
              className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center px-4"
              aria-hidden="true"
            >
              <span className="text-[10px] md:text-[11px] font-medium text-foreground-secondary uppercase tracking-wider">
                {isRTL ? "إجمالي المنفق" : "Total Spent"}
              </span>
              <span className="text-lg sm:text-xl font-extrabold text-foreground tracking-tight mt-0.5">
                {formatCurrency(totalAmount, locale)}
              </span>
              {totalCount > 0 && (
                <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-muted text-[10px] font-semibold text-foreground-secondary">
                  {totalCount} {isRTL ? "عملية صرف" : "transactions"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right: Detailed Initiative Breakdown List */}
        <div className="md:col-span-7 space-y-3.5">
          {/* Top Spender Highlight Banner */}
          {topItem && (
            <div className="p-3.5 rounded-xl border border-primary/20 bg-primary/5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                    {isRTL ? "المبادرة الأكثر استهلاكاً" : "Top Spending Initiative"}
                  </p>
                  <p className="text-sm font-bold text-foreground">
                    {getCategoryLabel(topItem.type, isRTL)}
                  </p>
                </div>
              </div>
              <div className="text-end flex-shrink-0">
                <span className="text-base font-extrabold text-primary">
                  {topItem.percentage.toFixed(0)}%
                </span>
                <p className="text-[11px] text-foreground-secondary">
                  {formatCurrency(topItem.numericAmount, locale)}
                </p>
              </div>
            </div>
          )}

          {/* Category List */}
          <div className="space-y-3">
            {items.map((item, index) => {
              const Icon = getCategoryIcon(item.type);
              const label = getCategoryLabel(item.type, isRTL);
              return (
                <div
                  key={index}
                  className="p-3.5 rounded-xl border border-border bg-background/50 hover:bg-muted/40 transition-colors space-y-2.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{
                          backgroundColor: `${item.color}15`,
                          color: item.color,
                        }}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {label}
                        </p>
                        <p className="text-xs text-foreground-secondary">
                          {item.count || 0} {isRTL ? "عملية مسجلة" : "transactions"}
                        </p>
                      </div>
                    </div>

                    {/* Amount & Percentage */}
                    <div className="text-end flex-shrink-0">
                      <p className="text-sm font-bold text-foreground">
                        {formatCurrency(item.numericAmount, locale)}
                      </p>
                      <p className="text-xs font-semibold" style={{ color: item.color }}>
                        {item.percentage.toFixed(1)}%
                      </p>
                    </div>
                  </div>

                  {/* Visual Progress Bar */}
                  <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${Math.min(Math.max(item.percentage, 2), 100)}%`,
                        backgroundColor: item.color,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
